import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { Task } from 'ember-concurrency';
import type { CountryCode } from 'frontend-utils/utils/countries';
import GeneralSelectionsMealPlan from 'lumen/components/general/selections/meal-plan';
import ParityDropdownsFromDateTypeSelection from 'lumen/components/parity/dropdowns/from-date-type-selection';
import ParityDropdownsPosSelection from 'lumen/components/parity/dropdowns/pos-selection';
import { AVAILABLE_MEAL_PLANS } from 'lumen/components/parity/selections';
import type {
  SelectionsKey,
  SelectionsValue,
} from 'lumen/controllers/hotel/parity/insight';
import type { ParityConfig } from 'lumen/models';
import type { MealTypeValues } from 'lumen/utils/constants/general-constants';
import {
  type CountryCodeWithDefault,
  DEFAULT_LOS,
  type FromDateType,
  RATE_TYPE_ICONS,
  RATE_TYPE_TRANSLATIONS,
  RATE_TYPES,
  type RateType,
  type SelectInputOptionWithId,
} from 'lumen/utils/constants/pi-constant-values';

export type SelectionsOption = SelectInputOptionWithId<SelectionsValue>;

const AVAILABLE_RATE_TYPES = [RATE_TYPES.LOWEST, RATE_TYPES.BEST_FLEX];

interface ParityInsightSelectionsSignature {
  Args: {
    config: Task<ParityConfig | null, []>;
    selectedFromDateType: FromDateType;
    selectedLos: number;
    selectedMealPlan: MealTypeValues;
    selectedPos: CountryCodeWithDefault;
    selectedRateType: RateType;
    selectionsChanged: (
      queryParam: SelectionsKey,
      option: SelectionsValue,
    ) => void;
  };
}

export default class ParityInsightSelections extends Component<ParityInsightSelectionsSignature> {
  @service declare intl: Services['intl'];

  @cached // Preserve reference for isEqual check in PowerSelect code
  get availableLos(): SelectInputOptionWithId<number>[] {
    if (!this.hasMultipleLosValues) {
      return [this.defaultLos];
    }

    return [
      this.defaultLos,
      ...this.losValues.map((los) => ({
        id: los,
        label: this.intl.t('rates.nights', { count: los }),
      })),
    ];
  }

  @cached // Preserve reference for isEqual check in PowerSelect code
  get availableRateTypes(): SelectInputOptionWithId<RateType>[] {
    return AVAILABLE_RATE_TYPES.map((rateType) => ({
      id: rateType,
      label: this.intl.t(RATE_TYPE_TRANSLATIONS[rateType]),
      icon: RATE_TYPE_ICONS[rateType],
    }));
  }

  get defaultLos(): SelectInputOptionWithId<number> {
    return {
      id: DEFAULT_LOS,
      label: this.intl.t('general.default_los'),
    };
  }

  get hasFailedFetchingConfig(): boolean {
    if (!this.args.config.last) {
      return false;
    }

    return this.args.config.last.isError;
  }

  get hasFetchedConfig(): boolean {
    return !!this.args.config.last?.value;
  }

  get hasMultipleLosValues(): boolean {
    return this.losValues.length > 1;
  }

  get isFetchingConfig(): boolean {
    return this.args.config.isRunning;
  }

  get isLosDropdownDisabled(): boolean {
    return !this.hasFetchedConfig || !this.hasMultipleLosValues;
  }

  get losDisabledMessage(): string | undefined {
    if (this.hasFailedFetchingConfig) {
      return this.intl.t('general.error_short');
    }

    if (this.showLOSUpsellCta) {
      return this.intl.t('parity.multi_los.cta');
    }

    return undefined;
  }

  get losValues(): number[] {
    if (!this.hasFetchedConfig) {
      return [];
    }

    return this.args.config.last?.value?.losValues ?? [];
  }

  get posValues(): CountryCode[] {
    if (!this.hasFetchedConfig) {
      return [];
    }

    return this.args.config.last?.value?.posValues ?? [];
  }

  get selectedLos(): SelectInputOptionWithId<number> | undefined {
    // Custom LOS can't be displayed until config has been fetched
    if (this.isFetchingConfig) {
      return this.defaultLos;
    }

    return this.availableLos.find((los) => los.id === this.args.selectedLos);
  }

  get selectedRateType(): SelectInputOptionWithId<RateType> | undefined {
    return this.availableRateTypes.find(
      (rateType) => rateType.id === this.args.selectedRateType,
    );
  }

  get showLOSUpsellCta(): boolean {
    return this.hasFetchedConfig && !this.hasMultipleLosValues;
  }

  @action selectionsChanged(
    queryParam: SelectionsKey,
    value: SelectionsOption,
  ): void {
    this.args.selectionsChanged(queryParam, value.id);
  }

  <template>
    <form class="flex grow items-center gap-200">
      <span class="ic-product-tour-48334237-multi-rate-type-dropdown">
        <PrismSelectInput
          @onChange={{fn this.selectionsChanged "rateType"}}
          @options={{this.availableRateTypes}}
          @value={{this.selectedRateType}}
        />
      </span>
      <PrismSelectInput
        @disabled={{this.isLosDropdownDisabled}}
        @disabledMessage={{this.losDisabledMessage}}
        @onChange={{fn this.selectionsChanged "los"}}
        @options={{this.availableLos}}
        @value={{this.selectedLos}}
      />
      <span class="ic-product-tour-48334237-multi-meal-plan-dropdown">
        <GeneralSelectionsMealPlan
          @allMealPlans={{AVAILABLE_MEAL_PLANS}}
          @onChange={{fn @selectionsChanged "mealPlan"}}
          @selectedMealPlan={{@selectedMealPlan}}
        />
      </span>

      <ParityDropdownsPosSelection
        @hasError={{this.hasFailedFetchingConfig}}
        @isLoading={{this.isFetchingConfig}}
        @onChange={{fn this.selectionsChanged "pos"}}
        @posValues={{this.posValues}}
        @selectedPos={{@selectedPos}}
      />

      <ParityDropdownsFromDateTypeSelection
        @onChange={{fn @selectionsChanged "fromDateType"}}
        @selectedFromDateType={{@selectedFromDateType}}
        class="ml-auto"
      />
    </form>
  </template>
}
